/**
 * _calc_materials.js — browser mirror of application/utils/calculators/materials.py:
 * soil, mulch, gravel, pavers and turf.
 *
 * The gravel density is cited and dated in materials.py. Change this,
 * materials.py and tests/fixtures/calculator_cases.json together.
 */

import { ceilWhole, plainNumber, roundHalfUp, roundUp } from "./_calc_math.js";

const GRAVEL_TONNES_PER_M3 = 1.5;

function volumes(v) {
    const net = v.length * v.width * (v.depth / 1000);
    return [{ area: roundHalfUp(v.length * v.width, 2), net_volume: roundHalfUp(net, 2) },
            net * (1 + v.wastage / 100)];
}

function byVolume(v, description) {
    const [outputs, raw] = volumes(v);
    const order = roundUp(raw, 1);
    return { outputs: { ...outputs, order_volume: order },
             items: order > 0 ? [{ description, quantity: order }] : [], warnings: [] };
}

export function soil(v) {
    return byVolume(v, "Garden soil, m3");
}

export function mulch(v) {
    return byVolume(v, "Mulch, m3");
}

export function gravel(v) {
    const [outputs, order] = volumes(v);
    const tonnes = roundUp(order * GRAVEL_TONNES_PER_M3, 1);
    return {
        outputs: { ...outputs, order_volume: roundUp(order, 1), tonnes },
        items: tonnes > 0 ? [{ description: "Gravel, tonnes", quantity: tonnes }] : [],
        warnings: [],
    };
}

export function pavers(v) {
    const area = v.length * v.width;
    const face = (v.paver_length / 1000) * (v.paver_width / 1000);
    if (face <= 0) {
        return { outputs: { area: roundHalfUp(area, 2), pavers: null, sand: null },
                 items: [], warnings: [] };
    }
    const count = Math.ceil(roundHalfUp(area / face * (1 + v.wastage / 100), 6));
    const sand = roundUp(area * (v.bedding / 1000), 1);
    const items = count > 0
        ? [{ description: `Pavers, ${plainNumber(v.paver_length)} x `
                          + `${plainNumber(v.paver_width)} mm`, quantity: count },
           { description: "Bedding sand, m3", quantity: sand }]
        : [];
    return { outputs: { area: roundHalfUp(area, 2), pavers: count, sand }, items, warnings: [] };
}

export function turf(v) {
    const area = v.length * v.width;
    const order = ceilWhole(area * (1 + v.wastage / 100));
    return { outputs: { area: roundHalfUp(area, 2), order_area: order },
             items: order > 0 ? [{ description: "Turf, m2", quantity: order }] : [],
             warnings: [] };
}
